<template>
  <div class="qam-root">
    <!-- Backdrop -->
    <div v-if="drawerOpen" class="qam-backdrop" @click="closeDrawer"></div>

    <!-- Drawer Panel -->
    <transition name="qam-slide">
      <div v-if="drawerOpen" class="qam-drawer">
        <!-- Header -->
        <div class="qam-header">
          <div>
            <div class="qam-title">Quick Add Minutes</div>
            <div class="qam-subtitle">Add minutes to live or past meetings</div>
          </div>
          <button class="qam-close" @click="closeDrawer">&times;</button>
        </div>

        <div class="qam-body">
          <!-- Meeting Selector (hidden once a meeting is selected) -->
          <div v-if="!selectedMeeting" class="qam-section">
            <label class="qam-label">Select Meeting</label>
            <div class="qam-search-wrap">
              <input v-model="meetingSearch" class="qam-search" placeholder="Search meetings..." />
            </div>

            <div v-if="loadingMeetings" class="qam-loading">Loading meetings...</div>

            <div v-else class="qam-meeting-list">
              <!-- Live meetings (started but not ended) -->
              <template v-if="filteredLiveMeetings.length">
                <div class="qam-group-label">
                  <span class="qam-dot-live"></span> Live Now
                </div>
                <div
                  v-for="m in filteredLiveMeetings" :key="'live-'+m.id"
                  class="qam-meeting-item"
                  :class="{ selected: selectedMeeting && selectedMeeting.id === m.id }"
                  @click="selectMeeting(m)"
                >
                  <span class="qam-dot-live"></span>
                  <div class="qam-meeting-info">
                    <div class="qam-meeting-name">{{ m.meeting_name }}</div>
                    <div class="qam-meeting-date">{{ formatDate(m.date_of_meeting) }}</div>
                  </div>
                  <span class="qam-live-tag">LIVE</span>
                  <button class="qam-open-btn" @click.stop="openMeetingInNewTab(m, 'live')" title="Open in meeting module">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  </button>
                </div>
              </template>

              <!-- Upcoming meetings (not started, not ended) -->
              <template v-if="filteredUpcomingMeetings.length">
                <div class="qam-group-label">
                  <span class="qam-dot-upcoming"></span> Upcoming
                </div>
                <div
                  v-for="m in filteredUpcomingMeetings" :key="'upcoming-'+m.id"
                  class="qam-meeting-item"
                  :class="{ selected: selectedMeeting && selectedMeeting.id === m.id }"
                  @click="selectMeeting(m)"
                >
                  <span class="qam-dot-upcoming"></span>
                  <div class="qam-meeting-info">
                    <div class="qam-meeting-name">{{ m.meeting_name }}</div>
                    <div class="qam-meeting-date">{{ formatDate(m.date_of_meeting) }}</div>
                  </div>
                  <button class="qam-open-btn" @click.stop="openMeetingInNewTab(m, 'upcoming')" title="Open in meeting module">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  </button>
                </div>
              </template>

              <!-- Past meetings (ended) -->
              <template v-if="filteredPastMeetings.length">
                <div class="qam-group-label">
                  <span class="qam-dot-past"></span> Past Meetings
                </div>
                <div
                  v-for="m in filteredPastMeetings" :key="'past-'+m.id"
                  class="qam-meeting-item"
                  :class="{ selected: selectedMeeting && selectedMeeting.id === m.id }"
                  @click="selectMeeting(m)"
                >
                  <span class="qam-dot-past"></span>
                  <div class="qam-meeting-info">
                    <div class="qam-meeting-name">{{ m.meeting_name }}</div>
                    <div class="qam-meeting-date">{{ formatDate(m.date_of_meeting) }}</div>
                  </div>
                  <button class="qam-open-btn" @click.stop="openMeetingInNewTab(m, 'past')" title="Open in meeting module">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  </button>
                </div>
              </template>

              <div v-if="!filteredLiveMeetings.length && !filteredUpcomingMeetings.length && !filteredPastMeetings.length && !loadingMeetings" class="qam-empty">
                No meetings found
              </div>
            </div>
          </div>

          <!-- Selected meeting info + form -->
          <template v-if="selectedMeeting">
            <div class="qam-selected-banner">
              <div class="qam-selected-info">
                <div class="qam-selected-name">{{ selectedMeeting.meeting_name }}</div>
                <div class="qam-selected-meta">
                  {{ formatDate(selectedMeeting.date_of_meeting) }}
                  <span v-if="existingMinutesCount > 0" class="qam-minute-count">{{ existingMinutesCount }} existing</span>
                </div>
              </div>
              <button class="qam-change-meeting" @click="openMeetingInNewTab(selectedMeeting, selectedMeeting.is_meeting_started && !selectedMeeting.is_meeting_ended ? 'live' : 'past')" title="Open in meeting module">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              </button>
              <button class="qam-change-meeting" @click="changeMeeting" title="Change meeting">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="1 4 1 10 7 10"/>
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                </svg>
              </button>
            </div>

            <!-- Add Minute Form -->
            <div ref="minuteFormSection" class="qam-form-group">
              <label class="qam-label">Minute Text <span class="qam-req">*</span></label>
              <textarea
                ref="minuteTextarea"
                v-model="form.minutes_name"
                class="qam-textarea"
                placeholder="Type the minute details..."
                rows="2"
              ></textarea>
            </div>
            <div class="qam-form-row">
              <label class="qam-label-inline">Type</label>
              <div class="qam-pills">
                <button
                  v-for="t in minuteTypes" :key="t"
                  class="qam-pill"
                  :class="{ active: form.minutes_type === t, [t.toLowerCase()]: true }"
                  @click="form.minutes_type = t"
                >{{ t }}</button>
              </div>
            </div>
            <div v-if="form.minutes_type === 'Action'" class="qam-form-row-pair">
              <div class="qam-form-half">
                <label class="qam-label">Assignee</label>
                <select v-model="form.assigned_to" class="qam-select">
                  <option value="">-- None --</option>
                  <option v-for="u in users" :key="u.id" :value="u.name">{{ u.name }}</option>
                </select>
              </div>
              <div class="qam-form-half">
                <label class="qam-label">Due Date</label>
                <input type="date" v-model="form.due_date" class="qam-input" />
              </div>
            </div>
            <button class="qam-add-to-queue" :disabled="!form.minutes_name.trim()" @click="addToQueue">+ Add Minute</button>

            <!-- Queued Minutes -->
            <div v-if="minutesQueue.length" class="qam-queue">
              <div class="qam-queue-header">
                <div class="qam-queue-title">
                  <span class="qam-queue-count">{{ minutesQueue.length }}</span>
                  Minute{{ minutesQueue.length > 1 ? 's' : '' }} Ready
                </div>
                <button class="qam-queue-clear" @click="clearQueue">Clear All</button>
              </div>

              <div class="qam-queue-list">
                <div v-for="(item, i) in minutesQueue" :key="i" class="qam-queue-item">
                  <span class="qam-queue-type" :class="item.minutes_type.toLowerCase()">{{ item.minutes_type.charAt(0) }}</span>
                  <div class="qam-queue-detail">
                    <div class="qam-queue-text">{{ item.minutes_name }}</div>
                    <div class="qam-queue-meta">
                      {{ item.minutes_type }}
                      <template v-if="item.assigned_to"> &middot; {{ item.assigned_to }}</template>
                      <template v-if="item.due_date"> &middot; Due {{ formatDate(item.due_date) }}</template>
                    </div>
                  </div>
                  <button class="qam-queue-remove" @click="removeFromQueue(i)" title="Remove">&times;</button>
                </div>
              </div>

              <button
                class="qam-submit"
                :disabled="submitting"
                @click="submitAllMinutes"
              >
                {{ submitting ? 'Submitting...' : 'Submit ' + minutesQueue.length + ' Minute' + (minutesQueue.length > 1 ? 's' : '') }}
              </button>
            </div>
          </template>

          <!-- Existing Minutes -->
          <div v-if="existingMinutesList.length" class="qam-existing">
            <div class="qam-existing-header" @click="showExistingMinutes = !showExistingMinutes">
              <span class="qam-existing-title">Existing Minutes</span>
              <span class="qam-minute-count">{{ existingMinutesList.length }}</span>
              <svg style="margin-left:auto;flex-shrink:0;transition:transform 0.2s;" :style="{ transform: showExistingMinutes ? 'rotate(180deg)' : 'rotate(0deg)' }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
            <div v-if="showExistingMinutes" class="qam-existing-list">
              <div v-if="loadingMinutes" class="qam-loading">Loading...</div>
              <div v-else v-for="(item, i) in existingMinutesList" :key="item.id || i" class="qam-existing-item">
                <span class="qam-queue-type" :class="(item.minutes_type || 'discussion').toLowerCase()">{{ (item.minutes_type || 'D').charAt(0) }}</span>
                <div class="qam-queue-detail">
                  <div class="qam-queue-text">{{ item.minutes_name }}</div>
                  <div class="qam-queue-meta">
                    {{ item.minutes_type }}
                    <template v-if="item.assigned_to">&middot; {{ item.assigned_to }}</template>
                    <template v-if="item.due_date">&middot; Due {{ formatDate(item.due_date) }}</template>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recently Added -->
          <div v-if="recentlyAddedList.length" class="qam-recent">
            <div class="qam-recent-title">Submitted This Session</div>
            <div v-for="(item, i) in recentlyAddedList" :key="i" class="qam-recent-item">
              <span class="qam-recent-type" :class="item.minutes_type.toLowerCase()">{{ item.minutes_type.charAt(0) }}</span>
              <div class="qam-recent-detail">
                <div class="qam-recent-text">{{ item.minutes_name }}</div>
                <div class="qam-recent-meeting">{{ item.meeting_name }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'QuickAddMinutes',

  data: function() {
    return {
      drawerOpen: false,
      meetingSearch: '',
      submitting: false,
      loadingMeetings: false,
      loadingMinutes: false,
      loadingUsers: false,

      meetings: [],
      selectedMeeting: null,
      existingMinutesCount: 0,
      existingMinutesList: [],
      showExistingMinutes: true,
      users: [],
      recentlyAddedList: [],
      minutesQueue: [],

      form: {
        minutes_name: '',
        minutes_type: 'Discussion',
        assigned_to: '',
        due_date: '',
      },

      minuteTypes: ['Discussion', 'Action', 'Decision', 'Information'],
    };
  },

  computed: {
    // Filter meetings where logged user is an attendee/member
    myMeetings: function() {
      var uid = Number(localStorage.getItem('USER_ID'));
      return this.meetings.filter(function(m) {
        var members = m.members;
        if (typeof members === 'string') {
          try { members = JSON.parse(members); } catch(e) { members = []; }
        }
        if (!members || !Array.isArray(members) || members.length === 0) return false;
        return members.some(function(member) {
          return Number(member.employee_id) == uid;
        });
      });
    },

    liveMeetings: function() {
      var today = moment().format('YYYY-MM-DD');
      return this.myMeetings.filter(function(m) {
        return m.active == true &&
               m.is_meeting_ended == false &&
               m.meeting_end_date == null &&
               m.date_of_meeting && m.meeting_time &&
               !m.is_statutory &&
               (m.date_of_meeting == today || m.is_meeting_started == true);
      });
    },

    upcomingMeetings: function() {
      var today = moment().format('YYYY-MM-DD');
      return this.myMeetings.filter(function(m) {
        return m.active == true &&
               m.is_meeting_ended == false &&
               m.meeting_end_date == null &&
               m.date_of_meeting && m.meeting_time &&
               !m.is_statutory &&
               m.date_of_meeting > today;
      });
    },

    pastMeetings: function() {
      return this.myMeetings.filter(function(m) {
        return m.active == true && m.is_meeting_ended == true;
      });
    },

    filteredLiveMeetings: function() {
      var search = (this.meetingSearch || '').toLowerCase();
      if (!search) return this.liveMeetings;
      return this.liveMeetings.filter(function(m) {
        return (m.meeting_name || '').toLowerCase().indexOf(search) > -1;
      });
    },

    filteredUpcomingMeetings: function() {
      var search = (this.meetingSearch || '').toLowerCase();
      var list = this.upcomingMeetings;
      if (search) {
        list = list.filter(function(m) {
          return (m.meeting_name || '').toLowerCase().indexOf(search) > -1;
        });
      }
      return list.slice(0, 10);
    },

    filteredPastMeetings: function() {
      var search = (this.meetingSearch || '').toLowerCase();
      var list = this.pastMeetings;
      if (search) {
        list = list.filter(function(m) {
          return (m.meeting_name || '').toLowerCase().indexOf(search) > -1;
        });
      }
      return list.slice(0, 20);
    },

    recentlyAdded: function() {
      return this.recentlyAddedList.length;
    },
  },

  methods: {
    toggleDrawer: function() {
      if (this.drawerOpen) {
        this.closeDrawer();
      } else {
        this.openDrawer();
      }
    },

    openDrawer: function() {
      this.drawerOpen = true;
      this.fetchMeetings();
      if (this.users.length === 0) {
        this.fetchUsers();
      }
    },

    closeDrawer: function() {
      this.drawerOpen = false;
    },

    fetchMeetings: function() {
      var self = this;
      var entityId = localStorage.getItem('ENTITY_ID');
      var userId = localStorage.getItem('USER_ID');
      self.loadingMeetings = true;
      axios.get('/get_meeting_landing_details', {
        params: {
          entity_id: entityId,
          from_dashboard: false,
          logged_user_id: Number(userId),
          page: 1,
          pagesize: 100,
          type: 'meeting',
          include_statutory: true,
          is_off_set: 'true',
          count_flag: 'true',
          text_search: '',
        }
      }).then(function(res) {
        var data = (res.data && res.data.data) || [];
        var allMeetings = Array.isArray(data) ? data : [data];
        // Normalize members field on each meeting
        self.meetings = allMeetings.map(function(m) {
          var members = m.members;
          if (typeof members === 'string') {
            try { members = JSON.parse(members); } catch(e) { members = []; }
          }
          m.members = Array.isArray(members) ? members : [];
          return m;
        });
        console.log('[QuickAddMinutes] Total from API:', allMeetings.length, '| After member filter:', self.myMeetings.length, '| USER_ID:', userId);
      }).catch(function(err) {
        console.error('[QuickAddMinutes] fetchMeetings error:', err);
      }).finally(function() {
        self.loadingMeetings = false;
      });
    },

    fetchUsers: function() {
      var self = this;
      var entityId = localStorage.getItem('ENTITY_ID');
      self.loadingUsers = true;
      axios.get('/employee', {
        params: { active: true, entity_id: entityId }
      }).then(function(res) {
        self.users = (res.data && res.data.data) || [];
      }).catch(function(err) {
        console.error('[QuickAddMinutes] fetchUsers error:', err);
      }).finally(function() {
        self.loadingUsers = false;
      });
    },

    selectMeeting: function(meeting) {
      this.selectedMeeting = meeting;
      this.fetchExistingMinutes(meeting.id);
      // Auto-scroll to the minute text field after DOM updates
      var self = this;
      this.$nextTick(function() {
        if (self.$refs.minuteFormSection) {
          self.$refs.minuteFormSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        if (self.$refs.minuteTextarea) {
          self.$refs.minuteTextarea.focus();
        }
      });
    },

    changeMeeting: function() {
      this.selectedMeeting = null;
      this.existingMinutesCount = 0;
      this.existingMinutesList = [];
      this.showExistingMinutes = true;
      this.minutesQueue = [];
      this.form.minutes_name = '';
      this.form.minutes_type = 'Discussion';
      this.form.assigned_to = '';
      this.form.due_date = '';
    },

    addToQueue: function() {
      if (!this.form.minutes_name.trim()) return;
      this.minutesQueue.push({
        minutes_name: this.form.minutes_name.trim(),
        minutes_type: this.form.minutes_type,
        assigned_to: this.form.assigned_to || '',
        due_date: this.form.due_date || '',
      });
      // Reset form for next entry
      this.form.minutes_name = '';
      this.form.minutes_type = 'Discussion';
      this.form.assigned_to = '';
      this.form.due_date = '';
      // Focus back on textarea for quick next entry
      var self = this;
      this.$nextTick(function() {
        if (self.$refs.minuteTextarea) {
          self.$refs.minuteTextarea.focus();
        }
      });
    },

    removeFromQueue: function(index) {
      this.minutesQueue.splice(index, 1);
    },

    clearQueue: function() {
      this.minutesQueue = [];
    },

    fetchExistingMinutes: function(meetingId) {
      var self = this;
      var entityId = localStorage.getItem('ENTITY_ID');
      var userId = localStorage.getItem('USER_ID');
      self.loadingMinutes = true;
      self.existingMinutesCount = 0;
      self.existingMinutesList = [];
      axios.get('/get_minutes_details', {
        params: {
          entity_id: entityId,
          meeting_id: meetingId,
          user_id: userId,
        }
      }).then(function(res) {
        var data = (res.data && res.data.data) || [];
        self.existingMinutesCount = data.length;
        self.existingMinutesList = data;
      }).catch(function(err) {
        console.error('[QuickAddMinutes] fetchExistingMinutes error:', err);
        self.existingMinutesCount = 0;
        self.existingMinutesList = [];
      }).finally(function() {
        self.loadingMinutes = false;
      });
    },

    submitAllMinutes: function() {
      var self = this;
      if (!self.minutesQueue.length) {
        self.global_push_toast('error', 'No minutes to submit');
        return;
      }
      if (!self.selectedMeeting) {
        self.global_push_toast('error', 'Please select a meeting');
        return;
      }

      self.submitting = true;
      var entityId = Number(localStorage.getItem('ENTITY_ID'));
      var userId = Number(localStorage.getItem('USER_ID'));
      var today = moment().format('YYYY-MM-DD');

      var payload = {
        add_minutes_details: self.minutesQueue.map(function(item) {
          return {
            meeting_id: self.selectedMeeting.id,
            entity_id: entityId,
            minutes_name: item.minutes_name,
            minutes_type: item.minutes_type,
            minutes_status: 'Open',
            minute_date: today,
            created_by: userId,
            active: true,
            from_dashboard: false,
            agenda_name: '',
            assigned_to: item.assigned_to || '',
            due_date: item.due_date || '',
            minutes_html: '',
            file_details: [],
            upload_image_file: [],
          };
        }),
      };

      var queueCount = self.minutesQueue.length;

      axios.post('/multi_insert_add_minutes_details', payload)
        .then(function() {
          self.global_push_toast('success', queueCount + ' minute' + (queueCount > 1 ? 's' : '') + ' added successfully');
          // Move submitted items to recently added
          for (var i = 0; i < self.minutesQueue.length; i++) {
            self.recentlyAddedList.unshift({
              minutes_name: self.minutesQueue[i].minutes_name,
              minutes_type: self.minutesQueue[i].minutes_type,
              meeting_name: self.selectedMeeting.meeting_name,
            });
          }
          if (self.recentlyAddedList.length > 10) {
            self.recentlyAddedList = self.recentlyAddedList.slice(0, 10);
          }
          self.minutesQueue = [];
          // Refresh existing minutes list to show newly added ones
          self.fetchExistingMinutes(self.selectedMeeting.id);
          // Focus textarea for next entry
          self.$nextTick(function() {
            if (self.$refs.minuteTextarea) {
              self.$refs.minuteTextarea.focus();
            }
          });
        })
        .catch(function(err) {
          console.error('[QuickAddMinutes] submitAllMinutes error:', err);
          self.global_push_toast('error', 'Failed to submit minutes');
        })
        .finally(function() {
          self.submitting = false;
        });
    },

    openMeetingInNewTab: function(meeting, mode) {
      // live → Minutes tab (edit), past/upcoming → Overview tab (view)
      var activeTab = mode === 'live' ? 1 : 0;
      // Store meeting data so the new tab can use it directly (avoids extra API call)
      try {
        localStorage.setItem('__qam_pending_meeting__', JSON.stringify({
          meeting: meeting,
          ts: Date.now()
        }));
      } catch(e) {}
      var url = '/meetings_new?openMinutes=true&meetingId=' + meeting.id + '&activeTab=' + activeTab;
      window.open(url, '_blank');
    },

    formatDate: function(dateStr) {
      if (!dateStr) return '';
      return moment(dateStr).format('DD MMM YYYY');
    },
  },
};
</script>

<style scoped>
.qam-root {
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

/* ── Backdrop ────────────────────────────────────────── */
.qam-backdrop {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 9997;
  background: rgba(0,0,0,.15);
}

/* ── Drawer ──────────────────────────────────────────── */
.qam-drawer {
  position: fixed;
  top: 68px;
  right: 16px;
  z-index: 9999;
  width: 420px;
  max-height: calc(100vh - 80px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 16px 56px rgba(0,0,0,.16), 0 2px 8px rgba(0,0,0,.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Slide transition */
.qam-slide-enter-active, .qam-slide-leave-active {
  transition: transform 0.22s ease, opacity 0.22s ease;
}
.qam-slide-enter, .qam-slide-leave-to {
  transform: translateY(-16px) scale(0.97);
  opacity: 0;
}

/* ── Header ──────────────────────────────────────────── */
.qam-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: linear-gradient(135deg, #1e2b6e, #3B4CC0);
  color: #fff;
  flex-shrink: 0;
}
.qam-title { font-size: 14px; font-weight: 700; }
.qam-subtitle { font-size: 10px; opacity: 0.7; margin-top: 1px; }
.qam-close {
  background: rgba(255,255,255,.15);
  border: none;
  color: #fff;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  flex-shrink: 0;
}
.qam-close:hover { background: rgba(255,255,255,.25); }

/* ── Body ────────────────────────────────────────────── */
.qam-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;
  scrollbar-width: thin;
  scrollbar-color: #d5d3de transparent;
}
.qam-body::-webkit-scrollbar { width: 4px; }
.qam-body::-webkit-scrollbar-thumb { background: #d5d3de; border-radius: 4px; }

/* ── Sections & Labels ───────────────────────────────── */
.qam-section { margin-bottom: 6px; }
.qam-label {
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 2px;
  display: block;
}
.qam-req { color: #E8553A; }

/* ── Compact Form Layout ─────────────────────────────── */
.qam-form-group { margin-bottom: 5px; }
.qam-form-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
}
.qam-label-inline {
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  flex-shrink: 0;
}
.qam-form-row-pair {
  display: flex;
  gap: 8px;
  margin-bottom: 5px;
}
.qam-form-half { flex: 1; min-width: 0; }

/* ── Search ──────────────────────────────────────────── */
.qam-search-wrap { margin-bottom: 5px; }
.qam-search {
  width: 100%;
  padding: 6px 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  font-size: 12px;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.qam-search:focus { border-color: #3B4CC0; }

/* ── Meeting List ────────────────────────────────────── */
.qam-meeting-list {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #f3f4f6;
  border-radius: 6px;
  scrollbar-width: thin;
}
.qam-meeting-list::-webkit-scrollbar { width: 3px; }
.qam-meeting-list::-webkit-scrollbar-thumb { background: #d5d3de; border-radius: 3px; }

.qam-group-label {
  font-size: 9px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding: 5px 10px 3px;
  display: flex;
  align-items: center;
  gap: 5px;
  background: #fafafa;
  position: sticky;
  top: 0;
  z-index: 1;
}

.qam-meeting-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  cursor: pointer;
  transition: background 0.12s;
  border-bottom: 1px solid #f9fafb;
}
.qam-meeting-item:hover { background: #f5f7ff; }
.qam-meeting-item.selected { background: #eef2ff; }

.qam-meeting-info { flex: 1; min-width: 0; }
.qam-meeting-name {
  font-size: 12px;
  font-weight: 500;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.qam-meeting-date { font-size: 10px; color: #9ca3af; margin-top: 0; }

.qam-dot-live {
  width: 7px; height: 7px; border-radius: 50%;
  background: #10B981;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px rgba(16,185,129,.15);
}
.qam-dot-upcoming {
  width: 7px; height: 7px; border-radius: 50%;
  background: #F59E0B;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px rgba(245,158,11,.12);
}
.qam-dot-past {
  width: 7px; height: 7px; border-radius: 50%;
  background: #d1d5db;
  flex-shrink: 0;
}

/* ── Open in Meeting Module Button ───────────────────── */
.qam-open-btn {
  width: 22px;
  height: 22px;
  border-radius: 5px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
  padding: 0;
  opacity: 0;
}
.qam-meeting-item:hover .qam-open-btn { opacity: 1; }
.qam-open-btn:hover { background: #eef2ff; color: #3B4CC0; border-color: #c7d2fe; }

.qam-live-tag {
  font-size: 8px;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
  background: #10B981;
  color: #fff;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  animation: qam-pulse 2s ease-in-out infinite;
}
@keyframes qam-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}
.qam-loading {
  text-align: center;
  padding: 12px;
  font-size: 11px;
  color: #9ca3af;
}
.qam-empty {
  text-align: center;
  padding: 12px;
  font-size: 11px;
  color: #9ca3af;
}

/* ── Selected Meeting Banner ─────────────────────────── */
.qam-selected-banner {
  background: #f0f4ff;
  border: 1px solid #dbe4ff;
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.qam-selected-info { flex: 1; min-width: 0; }
.qam-selected-name { font-size: 12px; font-weight: 600; color: #1e2b6e; }
.qam-selected-meta { font-size: 10px; color: #6b7280; margin-top: 1px; display: flex; align-items: center; gap: 6px; }
.qam-change-meeting {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid #dbe4ff;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
}
.qam-change-meeting:hover { background: #e8ecff; color: #1e2b6e; border-color: #b4c3ff; }
.qam-minute-count {
  background: rgba(59,76,192,.08);
  color: #3B4CC0;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 100px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* ── Form Controls ───────────────────────────────────── */
.qam-textarea {
  width: 100%;
  padding: 6px 8px;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  font-size: 12px;
  font-family: inherit;
  resize: vertical;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
  min-height: 44px;
}
.qam-textarea:focus { border-color: #3B4CC0; }

.qam-select, .qam-input {
  width: 100%;
  padding: 5px 8px;
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  font-size: 11.5px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
  background: #fff;
}
.qam-select:focus, .qam-input:focus { border-color: #3B4CC0; }

/* ── Type Pills ──────────────────────────────────────── */
.qam-pills { display: flex; gap: 4px; flex-wrap: wrap; }
.qam-pill {
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 10.5px;
  font-weight: 600;
  border: 1.5px solid #e5e7eb;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}
.qam-pill:hover { border-color: #c7cad1; }
.qam-pill.active.discussion { background: #eef2ff; border-color: #3B4CC0; color: #3B4CC0; }
.qam-pill.active.action { background: #fef3c7; border-color: #F59E0B; color: #b45309; }
.qam-pill.active.decision { background: #d1fae5; border-color: #10B981; color: #065f46; }
.qam-pill.active.information { background: #f3e8ff; border-color: #8B5CF6; color: #6d28d9; }

/* ── Add to Queue Button ─────────────────────────────── */
.qam-add-to-queue {
  width: 100%;
  padding: 6px;
  border: 1.5px dashed #3B4CC0;
  border-radius: 6px;
  background: #f5f7ff;
  color: #3B4CC0;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
  margin-bottom: 6px;
  margin-top: 2px;
}
.qam-add-to-queue:hover { background: #eef2ff; border-color: #1e2b6e; }
.qam-add-to-queue:disabled { opacity: 0.4; cursor: not-allowed; border-color: #d1d5db; color: #9ca3af; background: #fafafa; }

/* ── Queue ───────────────────────────────────────────── */
.qam-queue {
  background: #fafbff;
  border: 1px solid #e8ecff;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 6px;
}
.qam-queue-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.qam-queue-title {
  font-size: 11px;
  font-weight: 600;
  color: #1e2b6e;
  display: flex;
  align-items: center;
  gap: 5px;
}
.qam-queue-count {
  background: #3B4CC0;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.qam-queue-clear {
  font-size: 10px;
  font-weight: 600;
  color: #ef4444;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.15s;
}
.qam-queue-clear:hover { background: #fef2f2; }

.qam-queue-list {
  max-height: 140px;
  overflow-y: auto;
  scrollbar-width: thin;
  margin-bottom: 6px;
}
.qam-queue-list::-webkit-scrollbar { width: 3px; }
.qam-queue-list::-webkit-scrollbar-thumb { background: #d5d3de; border-radius: 3px; }

.qam-queue-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 5px 6px;
  background: #fff;
  border-radius: 5px;
  margin-bottom: 4px;
  border: 1px solid #f0f1f4;
}
.qam-queue-item:last-child { margin-bottom: 0; }

.qam-queue-type {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  flex-shrink: 0;
  color: #fff;
}
.qam-queue-type.discussion { background: #3B4CC0; }
.qam-queue-type.action { background: #F59E0B; }
.qam-queue-type.decision { background: #10B981; }
.qam-queue-type.information { background: #8B5CF6; }

.qam-queue-detail { flex: 1; min-width: 0; }
.qam-queue-text {
  font-size: 11px;
  font-weight: 500;
  color: #1f2937;
  word-break: break-word;
}
.qam-queue-meta {
  font-size: 9.5px;
  color: #9ca3af;
  margin-top: 1px;
}
.qam-queue-remove {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: none;
  color: #d1d5db;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
  font-family: inherit;
}
.qam-queue-remove:hover { background: #fef2f2; color: #ef4444; }

/* ── Submit ──────────────────────────────────────────── */
.qam-submit {
  width: 100%;
  padding: 8px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #1e2b6e, #3B4CC0);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.15s, transform 0.1s;
}
.qam-submit:hover { opacity: 0.92; }
.qam-submit:active { transform: scale(0.98); }
.qam-submit:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── Existing Minutes ────────────────────────────────── */
.qam-existing {
  border: 1px solid #e8ecff;
  border-radius: 8px;
  margin-bottom: 6px;
  overflow: hidden;
}
.qam-existing-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #f8f9ff;
  cursor: pointer;
  user-select: none;
}
.qam-existing-header:hover { background: #f0f4ff; }
.qam-existing-title {
  font-size: 10px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.qam-existing-list {
  max-height: 200px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #d5d3de transparent;
  padding: 4px 6px;
}
.qam-existing-list::-webkit-scrollbar { width: 3px; }
.qam-existing-list::-webkit-scrollbar-thumb { background: #d5d3de; border-radius: 3px; }
.qam-existing-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 5px 6px;
  border-radius: 5px;
  border-bottom: 1px solid #f0f1f4;
}
.qam-existing-item:last-child { border-bottom: none; }

/* ── Recently Added ──────────────────────────────────── */
.qam-recent {
  border-top: 1px solid #f3f4f6;
  padding-top: 6px;
  margin-top: 2px;
}
.qam-recent-title {
  font-size: 9px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
}
.qam-recent-item {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 3px 0;
}
.qam-recent-type {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  flex-shrink: 0;
  color: #fff;
}
.qam-recent-type.discussion { background: #3B4CC0; }
.qam-recent-type.action { background: #F59E0B; }
.qam-recent-type.decision { background: #10B981; }
.qam-recent-type.information { background: #8B5CF6; }
.qam-recent-detail { flex: 1; min-width: 0; }
.qam-recent-text {
  font-size: 11px;
  font-weight: 500;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.qam-recent-meeting { font-size: 9.5px; color: #9ca3af; margin-top: 0; }

/* ── Responsive ──────────────────────────────────────── */
@media (max-width: 480px) {
  .qam-drawer {
    width: calc(100vw - 16px);
    right: 8px;
    top: 60px;
  }
}
</style>
